import { cn } from "@/lib/utils";
import { motion, useReducedMotion } from "motion/react";
import type { ReactNode } from "react";

const ease = [0.22, 1, 0.36, 1] as const;

export function Reveal({
  children,
  className,
  delay = 0,
  as = "div",
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  as?: "div" | "li";
}) {
  const reduce = useReducedMotion() === true;
  const Tag = as === "li" ? motion.li : motion.div;
  return (
    <Tag
      data-reveal
      className={className}
      initial={reduce ? false : { opacity: 0, y: 28 }}
      whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.18 }}
      transition={{ duration: 0.55, delay, ease }}
    >
      {children}
    </Tag>
  );
}

export function LandingSection({
  id,
  eyebrow,
  title,
  lede,
  children,
  className,
}: {
  id: string;
  eyebrow: string;
  title: string;
  lede?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      id={id}
      data-landing-section={id}
      className={cn(
        "relative scroll-mt-[72px] border-t border-white/10 bg-black px-6 py-24 sm:px-10 sm:py-32",
        className,
      )}
    >
      <div className="mx-auto max-w-[1080px]">
        <Reveal>
          <p className="mb-3 text-[11px] font-semibold tracking-[0.16em] text-falcon uppercase">{eyebrow}</p>
          <h2 className="max-w-[720px] text-[32px] leading-[1.1] font-bold tracking-[-0.02em] text-white sm:text-[40px]">
            {title}
          </h2>
          {lede ? <p className="mt-4 max-w-[640px] text-[16px] leading-[1.6] text-white/68">{lede}</p> : null}
        </Reveal>
        <div className="mt-10">{children}</div>
      </div>
    </section>
  );
}

export const glass =
  "rounded-[4px] border border-white/12 bg-white/[0.04] backdrop-blur-[14px] shadow-[0_16px_48px_rgba(0,0,0,0.35)]";
